import React, { FC, ReactNode } from 'react';
import { Link } from 'src/components/widgets/buttons';
import { Icon } from 'src/components/widgets/icon';
import { NotificationBanner } from 'src/components/widgets/notificationBanner';
import { T, backofficeAnonymousUrl } from 'src/shared/helpers';
import ErrorOutlineIcon from 'src/shared/material_icons/alert/error_outline.svg';
import styled from 'styled-components';
import { SellerPlanCheckInfo } from './switchTypes';

/**
 * Banner markup for the switch confirmation dialog. Markup only — the buttons and the flow logic stay in
 * switchSalesChannelDialog.tsx, which is what keeps these props lists small.
 *
 * Blocking product messages use the same structured content style as the switch wizard, while plan checks remain
 * critical banners because they describe a subscription constraint.
 */

const SALES_CHANNELS_HELP_ANCHOR_URL =
    'https://zakeke.zendesk.com/hc/en-us/articles/4415810409106-Sales-Channels?brand_id=2771725#h_01G5S8E6VBXQJ5BDYFM50NZ8BA';
const PUBLISHED_PRODUCTS_HELP_URL = 'https://zakeke.zendesk.com/hc/en-us/articles/360022582493-What-do-you-mean-by-Published-Products';

export const AlertSectionDivider = styled.hr`
    border: none;
    border-top: 2px solid #e0e0e0;
    margin: 4px 0;
`;

export const PlanCheckAlertSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px;
    border: 1px solid #ca0000;
    border-left: 4px solid #ff0000;
    border-radius: 4px;
`;

/**
 * A single CRITICAL banner boxed in the plan-check frame, followed by a divider. The divider is a sibling of the
 * section in DialogContent's flow, not a child of it.
 */
const PlanCheckAlert: FC<{ title: string; content: ReactNode }> = ({ title, content }) => (
    <>
        <PlanCheckAlertSection>
            <NotificationBanner type='CRITICAL' title={title} content={content} />
        </PlanCheckAlertSection>
        <AlertSectionDivider />
    </>
);

/** The "you must unpublish N products" pair plus its Learn-more link — identical in both product-limit banners. */
const PublishedProductsOverageDetails: FC<{ planCheck: SellerPlanCheckInfo }> = ({ planCheck }) => (
    <>
        {planCheck.maxProductsScale != null && planCheck.currentProductCount - planCheck.maxProductsScale > 0 && (
            <p>
                {T._('To complete the subscription to the Scale plan, you will need to unpublish', 'Admin')}{' '}
                {planCheck.currentProductCount - planCheck.maxProductsScale} {T._('product(s).', 'Admin')}
            </p>
        )}
        {planCheck.maxProductsGrow != null && planCheck.currentProductCount - planCheck.maxProductsGrow > 0 && (
            <p>
                {T._('To complete the subscription to the Grow plan, you will need to unpublish', 'Admin')}{' '}
                {planCheck.currentProductCount - planCheck.maxProductsGrow} {T._('product(s).', 'Admin')}
            </p>
        )}

        <Link href={PUBLISHED_PRODUCTS_HELP_URL} target={'_blank'}>
            {T._('Learn more', 'Admin')}
        </Link>
    </>
);

export const LegacyPlanNotSupportedAlert: FC = () => (
    <NotificationBanner
        type='CRITICAL'
        title={T._('Switch Not Available for Legacy Plans', 'Admin')}
        content={<p>{T._('The sales channel switch cannot be executed on your current plan. Please contact Zakeke support for assistance.', 'Admin')}</p>}
    />
);

const PodProductsAlert: FC = () => (
    <BlockingProductMessage>
        <BlockingProductTitle>{T._('Print-on-Demand Products Detected', 'Admin')}</BlockingProductTitle>
        <p>
            {T._(
                'Your account currently has active Print-on-Demand (POD) products. The sales channel switch cannot be completed while POD products are present, as they are bound to your current Master Integration.',
                'Admin'
            )}
        </p>
        <p>
            {T._('To proceed, please remove all Print-on-Demand products from your account. Once done, you will be able to continue with the switch.', 'Admin')}
        </p>
        <p>
            {T._(
                'After the switch is complete, you will need to set up the POD integration again on the new Master Integration in order to re-import your Print-on-Demand products.',
                'Admin'
            )}
        </p>
        <Link href={SALES_CHANNELS_HELP_ANCHOR_URL} target='_blank'>
            {T._('Learn more', 'Admin')}
        </Link>
    </BlockingProductMessage>
);

const ConfiguratorProductsAlert: FC = () => (
    <BlockingProductMessage>
        <BlockingProductTitle>{T._('Product Configurator Products Detected', 'Admin')}</BlockingProductTitle>
        <p>
            {T._(
                'Your account contains products using 3D Product Configurator, which is not compatible with the sales channel switch. Please remove these products before proceeding.',
                'Admin'
            )}
        </p>
        <Link href={SALES_CHANNELS_HELP_ANCHOR_URL} target='_blank'>
            {T._('Learn more', 'Admin')}
        </Link>
    </BlockingProductMessage>
);

const BlockingProductsSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 16px 0;
    border-top: 1px solid #dbe2e6;
    border-bottom: 1px solid #dbe2e6;
`;

const BlockingProductMessage = styled.section`
    display: flex;
    flex-direction: column;
    gap: 8px;
    color: #333;
    font-size: 13px;
    line-height: 18px;

    p {
        margin: 0;
    }
`;

const BlockingProductTitle = styled.h4`
    margin: 0;
    color: #ca0000;
    font-size: 14px;
    font-weight: 600;
`;

const WizardContentDivider = styled.hr`
    width: 100%;
    margin: 0;
    border: none;
    border-top: 1px solid #dbe2e6;
`;

/** Both blockers share the wizard's structured-content treatment, with a separator only when both are present. */
export const BlockingProductsAlerts: FC<{ hasPodProducts: boolean; hasConfiguratorProducts: boolean }> = ({ hasPodProducts, hasConfiguratorProducts }) => (
    <BlockingProductsSection>
        {hasPodProducts && <PodProductsAlert />}
        {hasPodProducts && hasConfiguratorProducts && <WizardContentDivider />}
        {hasConfiguratorProducts && <ConfiguratorProductsAlert />}
    </BlockingProductsSection>
);

export const ScalePlanRequiredAlert: FC = () => (
    <PlanCheckAlert
        title={T._('Scale Plan Subscription Required Before Switch', 'Admin')}
        content={
            <>
                <p>
                    {T._(
                        'The sales channel switch process includes a subscription step in which you will be asked to select a new Zakeke plan for your new Master Integration.',
                        'Admin'
                    )}
                </p>
                <p>
                    {T._(
                        'Based on your current number of published products, you will need to subscribe to at least the Scale plan to complete the switch.',
                        'Admin'
                    )}
                </p>
                <p>
                    {T._('Before proceeding, please verify that the', 'Admin')}{' '}
                    <Link href={backofficeAnonymousUrl('/pricing')} target='_blank'>
                        {T._('Scale plan', 'Admin')}
                    </Link>{' '}
                    {T._('is suitable for your needs. If you have any doubts, please contact Zakeke support.', 'Admin')}
                </p>
            </>
        }
    />
);

export const ProductLimitReachedAlert: FC<{ planCheck: SellerPlanCheckInfo }> = ({ planCheck }) => (
    <PlanCheckAlert
        title={T._('Product Limit Reached', 'Admin')}
        content={
            <>
                <p>
                    {T._(
                        'The sales channel switch process includes a subscription step in which you will be asked to select a new Zakeke plan for your new Master Integration.',
                        'Admin'
                    )}
                </p>
                <PublishedProductsOverageDetails planCheck={planCheck} />

                <br />
                <br />
                <p>
                    {T._('Please also check our', 'Admin')}{' '}
                    <Link href={backofficeAnonymousUrl('/pricing')} target='_blank'>
                        {T._('pricing page', 'Admin')}
                    </Link>{' '}
                    {T._('before proceeding. If in doubt, please contact Zakeke support.', 'Admin')}
                </p>
            </>
        }
    />
);

/**
 * Unreachable in practice: its call site sits in the branch where isV1PlanBlocked is already false, which makes
 * `!isNotV1ProductLimitReached && planCheck?.isProductLimitReached` a contradiction. Kept verbatim — see
 * docs/sales-channel-switch-refactor.md. Deliberately NOT merged with ProductLimitReachedAlert: the bodies differ,
 * so collapsing them would surface copy that has never rendered.
 */
export const ProductLimitReachedSimpleAlert: FC<{ planCheck: SellerPlanCheckInfo }> = ({ planCheck }) => (
    <PlanCheckAlert
        title={T._('Product Limit Reached', 'Admin')}
        content={
            <>
                <PublishedProductsOverageDetails planCheck={planCheck} />

                <br />
                <br />
                <p>{T._('If in doubt, please contact Zakeke support.', 'Admin')}</p>
            </>
        }
    />
);

const MigrationBlocker = styled.section`
    display: flex;
    gap: 16px;
    padding: 20px;
    border: 1px solid #f0b4b4;
    border-radius: 6px;
    background: #fff7f7;
`;

const MigrationBlockerIcon = styled(Icon)`
    width: 32px;
    height: 32px;
    min-width: 32px;
    color: #ca0000;

    svg {
        width: 32px;
        height: 32px;
    }
`;

const MigrationBlockerContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const MigrationBlockerMessage = styled.p`
    margin: 0;
    color: #333;
    font-size: 13px;
    line-height: 18px;
`;

const MigrationBlockerTitle = styled.h4`
    margin: 0;
    color: #ca0000;
    font-size: 14px;
    font-weight: 600;
`;

/** Shown when the two platforms cannot be switched automatically and only a manual migration is possible. */
export const MigrationOnlyAlerts: FC = () => (
    <MigrationBlocker>
        <MigrationBlockerIcon>
            <ErrorOutlineIcon />
        </MigrationBlockerIcon>
        <MigrationBlockerContent>
            <MigrationBlockerTitle>{T._('Manual migration required', 'Admin')}</MigrationBlockerTitle>
            <MigrationBlockerMessage>{T._('This sales channel cannot be set as the Master Integration automatically.', 'Admin')}</MigrationBlockerMessage>
            <MigrationBlockerMessage>
                {T._(
                    'The current Master Integration and the selected sales channel use different platforms. To protect your products and configuration, this switch must be completed manually by the Zakeke team.',
                    'Admin'
                )}
            </MigrationBlockerMessage>
        </MigrationBlockerContent>
    </MigrationBlocker>
);
